'use client';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import clsx from 'clsx';
import { type PointerEvent, type ReactNode } from 'react';

import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import { useMediaQueries } from '@commons/contexts/responsive';
import type { Breakpoint, Theme } from '@commons/types/styles';

import styles from './MegaMenu.module.scss';

// Type definition of the MegaMenu component properties.

type Props = {
  theme: Theme;
  children: ReactNode;
  trigger: ReactNode;
  triggerMode?: 'click' | 'hover';
  innerTrigger?: ReactNode;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  mobileBreakpoint?: Breakpoint;
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
};

/**
 * Desktop MegaMenu component.
 *
 * Can be used for displaying a mega menu with a trigger (menu link, locale switcher icon, etc).
 */

const MegaMenu = ({
  theme,
  children,
  trigger,
  triggerMode = 'hover',
  innerTrigger,
  isOpen,
  setIsOpen,
  mobileBreakpoint = 'md',
  className,
  containerClassName,
  contentClassName,
}: Props) => {
  const { ready, maxWidth } = useMediaQueries();

  const isMobile = ready && maxWidth[mobileBreakpoint];

  // Close the desktop mega menu on mobile.
  if (isOpen && isMobile) {
    setIsOpen(false);
  }

  const handleValueChange = (value: string) => {
    if (triggerMode === 'click') {
      setIsOpen(!!value);
    }
  };

  const handleHoverEvent = (event: PointerEvent) => {
    if (triggerMode === 'click') {
      event.preventDefault();
    }
  };

  // Ignore clicks on the cookie banner.
  const handleInteractOutside = (event: CustomEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.ot-sdk-container')) {
      event.preventDefault();
    }
  };

  const renderedInnerTrigger = innerTrigger ? (
    <NavigationMenu.Trigger
      asChild
      onPointerMove={handleHoverEvent}
      onPointerLeave={handleHoverEvent}
    >
      {innerTrigger}
    </NavigationMenu.Trigger>
  ) : null;

  const classes = clsx(styles.megaMenu, isOpen && 'activeDesktopMenu');
  const contentClasses = clsx(styles.content, isOpen && styles.active, 'h-100');

  return (
    <NavigationMenu.Root asChild aria-label={undefined} onValueChange={handleValueChange}>
      <div className={classes}>
        <NavigationMenu.List>
          <NavigationMenu.Item className={clsx('h-100', className)}>
            <NavigationMenu.Trigger
              asChild
              onPointerMove={handleHoverEvent}
              onPointerLeave={handleHoverEvent}
            >
              {trigger}
            </NavigationMenu.Trigger>
            <NavigationMenu.Content
              asChild
              onPointerEnter={handleHoverEvent}
              onPointerLeave={handleHoverEvent}
              onInteractOutside={handleInteractOutside}
              className={contentClasses}
            >
              <ResponsiveContainer
                theme={theme}
                paddingTop="xl"
                paddingBottom="xl"
                className={clsx(styles.wrapper, 'h-100', containerClassName)}
                contentClassName={contentClassName}
              >
                {children}
                {renderedInnerTrigger}
              </ResponsiveContainer>
            </NavigationMenu.Content>
          </NavigationMenu.Item>
        </NavigationMenu.List>
      </div>
    </NavigationMenu.Root>
  );
};

export default MegaMenu;
